import { isDecorative, touchesOnlyDecorativeSegments } from "./decorative-segments.ts";
import type { LngLat } from "./types.ts";

/**
 * Drawing problems that keep a route from saving or that likely are mistakes:
 * disconnected parts, lone points, near-miss dangling ends, doubled or
 * near-parallel lines, and lines that cross without a shared vertex.
 * Pure and allocation-light so the editor can rerun it after every edit.
 */

export interface RouteIssueOptions {
  /** A dangling end this close to an unconnected vertex or line is a gap. */
  gapM?: number;
  /** Two near-parallel lines this close to each other overlap. */
  overlapM?: number;
  /** Shortest shared stretch that counts as an overlap. */
  overlapMinM?: number;
  /** Largest angle between two lines that still counts as parallel. */
  parallelDeg?: number;
}

export interface GapIssue {
  /** The dangling (degree-1) vertex. */
  point: number;
  /** Closest spot it probably should join. */
  target: LngLat;
  targetPoint: number | null;
  targetSegment: number | null;
  distanceM: number;
}

export interface OverlapIssue {
  a: number;
  b: number;
  /** Middle of the shared stretch. */
  at: LngLat;
  lengthM: number;
}

export interface CrossingIssue {
  a: number;
  b: number;
  at: LngLat;
}

export interface ConnectedParts {
  count: number;
  /** Part id per vertex, 0..count-1. */
  partOf: Int32Array;
  /** Vertex count per part. */
  sizes: number[];
  /** Part with the most vertices (then the most lines). -1 when empty. */
  largest: number;
}

export interface RouteIssues {
  parts: ConnectedParts;
  /** Vertices outside the largest part. */
  strayPoints: number[];
  /** Lines outside the largest part. */
  straySegments: number[];
  /** Vertices with no line at all. */
  lonePoints: number[];
  gaps: GapIssue[];
  overlaps: OverlapIssue[];
  crossings: CrossingIssue[];
}

export const GAP_M = 25;
export const OVERLAP_M = 4;
export const OVERLAP_MIN_M = 6;
export const PARALLEL_DEG = 15;
/** Overlap and crossing spots this close count as one issue. */
export const ISSUE_CLUSTER_M = 30;

const M_PER_DEG = 111_320;
const NO_PARTS: ConnectedParts = { count: 0, partOf: new Int32Array(0), sizes: [], largest: -1 };

type Segs = ReadonlyArray<readonly [number, number]>;

export function connectedParts(n: number, segments: Segs): ConnectedParts {
  if (n === 0) return NO_PARTS;
  const parent = new Int32Array(n);
  for (let i = 0; i < n; i++) parent[i] = i;
  const find = (i: number): number => {
    let v = i;
    while (parent[v] !== v) {
      parent[v] = parent[parent[v]];
      v = parent[v];
    }
    return v;
  };
  for (const [a, b] of segments) {
    if (a < 0 || b < 0 || a >= n || b >= n) continue;
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[ra] = rb;
  }
  const idOfRoot = new Int32Array(n).fill(-1);
  const partOf = new Int32Array(n);
  const sizes: number[] = [];
  for (let i = 0; i < n; i++) {
    const r = find(i);
    if (idOfRoot[r] < 0) {
      idOfRoot[r] = sizes.length;
      sizes.push(0);
    }
    partOf[i] = idOfRoot[r];
    sizes[partOf[i]] += 1;
  }
  const lines = new Array<number>(sizes.length).fill(0);
  for (const [a] of segments) if (a >= 0 && a < n) lines[partOf[a]] += 1;
  let largest = 0;
  for (let p = 1; p < sizes.length; p++) {
    if (sizes[p] > sizes[largest] || (sizes[p] === sizes[largest] && lines[p] > lines[largest])) largest = p;
  }
  return { count: sizes.length, partOf, sizes, largest };
}

/** Vertices outside the largest part, ascending. */
export function smallerPartPoints(parts: ConnectedParts): number[] {
  const out: number[] = [];
  if (parts.count < 2) return out;
  for (let i = 0; i < parts.partOf.length; i++) {
    if (parts.partOf[i] < 0 || parts.partOf[i] !== parts.largest) out.push(i);
  }
  return out;
}

/**
 * Connected parts of the walkable route only. Decorative-only vertices and
 * decorative-only subgraphs do not add parts or stray warnings.
 */
export function routeConnectivityParts(
  n: number,
  segments: Segs,
  decorative?: readonly boolean[],
): ConnectedParts {
  const routeSegs: Array<[number, number]> = [];
  const decorDeg = new Int32Array(n);
  for (let s = 0; s < segments.length; s++) {
    const seg = segments[s];
    if (!seg) continue;
    if (isDecorative(decorative, s)) {
      decorDeg[seg[0]] += 1;
      decorDeg[seg[1]] += 1;
      continue;
    }
    routeSegs.push([seg[0], seg[1]]);
  }
  const onRoute = new Uint8Array(n);
  for (const [a, b] of routeSegs) {
    if (a >= 0 && a < n) onRoute[a] = 1;
    if (b >= 0 && b < n) onRoute[b] = 1;
  }
  if (routeSegs.length === 0 && n === 0) return NO_PARTS;
  const raw = routeSegs.length > 0 ? connectedParts(n, routeSegs) : { count: 0, partOf: new Int32Array(n).fill(-1), sizes: [], largest: -1 };
  const sizes = new Array<number>(raw.count).fill(0);
  for (let i = 0; i < n; i++) {
    if (onRoute[i] === 0) continue;
    sizes[raw.partOf[i]] += 1;
  }
  const remap = new Int32Array(raw.count).fill(-1);
  const compactSizes: number[] = [];
  for (let p = 0; p < raw.count; p++) {
    if (sizes[p] <= 0) continue;
    remap[p] = compactSizes.length;
    compactSizes.push(sizes[p]);
  }
  const partOf = new Int32Array(n).fill(-1);
  for (let i = 0; i < n; i++) {
    if (onRoute[i] === 0) continue;
    partOf[i] = remap[raw.partOf[i]];
  }
  for (let i = 0; i < n; i++) {
    if (onRoute[i] !== 0 || decorDeg[i] !== 0) continue;
    const id = compactSizes.length;
    compactSizes.push(1);
    partOf[i] = id;
  }
  if (compactSizes.length === 0) return NO_PARTS;
  const lines = new Array<number>(compactSizes.length).fill(0);
  for (let s = 0; s < segments.length; s++) {
    if (isDecorative(decorative, s)) continue;
    const seg = segments[s];
    if (!seg) continue;
    const p = partOf[seg[0]];
    if (p >= 0) lines[p] += 1;
  }
  let largest = 0;
  for (let p = 1; p < compactSizes.length; p++) {
    if (
      compactSizes[p] > compactSizes[largest] ||
      (compactSizes[p] === compactSizes[largest] && lines[p] > lines[largest])
    ) {
      largest = p;
    }
  }
  return { count: compactSizes.length, partOf, sizes: compactSizes, largest };
}

export function findRouteIssues(
  points: readonly LngLat[],
  segments: Segs,
  options: RouteIssueOptions = {},
  decorative?: readonly boolean[],
): RouteIssues {
  const gapM = options.gapM ?? GAP_M;
  const overlapM = options.overlapM ?? OVERLAP_M;
  const overlapMinM = options.overlapMinM ?? OVERLAP_MIN_M;
  const sinParallel = Math.sin(((options.parallelDeg ?? PARALLEL_DEG) * Math.PI) / 180);
  const n = points.length;
  const routeSegs: Array<[number, number]> = [];
  const routeSegIdx: number[] = [];
  for (let s = 0; s < segments.length; s++) {
    if (isDecorative(decorative, s)) continue;
    const seg = segments[s];
    if (!seg) continue;
    routeSegs.push([seg[0], seg[1]]);
    routeSegIdx.push(s);
  }
  const parts = routeConnectivityParts(n, segments, decorative);
  const strayPoints = smallerPartPoints(parts);
  const straySegments: number[] = [];
  if (parts.count > 1) {
    for (let s = 0; s < segments.length; s++) {
      if (isDecorative(decorative, s)) continue;
      const seg = segments[s];
      if (!seg) continue;
      const p = parts.partOf[seg[0]];
      if (p < 0 || p !== parts.largest) straySegments.push(s);
    }
  }
  const deg = new Int32Array(n);
  const decorDeg = new Int32Array(n);
  for (let s = 0; s < segments.length; s++) {
    const [a, b] = segments[s]!;
    if (isDecorative(decorative, s)) {
      decorDeg[a] += 1;
      decorDeg[b] += 1;
      continue;
    }
    deg[a] += 1;
    deg[b] += 1;
  }
  const lonePoints: number[] = [];
  if (n > 1) {
    for (let i = 0; i < n; i++) {
      if (deg[i] === 0 && decorDeg[i] === 0) lonePoints.push(i);
    }
  }

  const empty = { parts, strayPoints, straySegments, lonePoints, gaps: [], overlaps: [], crossings: [] };
  if (n === 0) return empty;

  const proj = new Projection(points);
  const { xs, ys } = proj;
  const cell = Math.max(gapM, 10);
  const pointGrid = new Map<number, number[]>();
  for (let i = 0; i < n; i++) push(pointGrid, cellKey(Math.floor(xs[i] / cell), Math.floor(ys[i] / cell)), i);
  const segGrid = new SegmentGrid(cell, xs, ys, routeSegs, Math.max(gapM, overlapM));

  const gaps = filterDecorativeGaps(
    findGaps(n, routeSegs, deg, xs, ys, proj, pointGrid, segGrid, cell, gapM),
    segments,
    decorative,
  );
  const { overlaps, crossings } = findOverlapsAndCrossings(
    routeSegs,
    routeSegIdx,
    xs,
    ys,
    proj,
    segGrid,
    overlapM,
    overlapMinM,
    sinParallel,
  );
  return { ...empty, gaps, overlaps, crossings };
}

/** Spots within `radiusM` of a cluster's first spot join it. Returns each cluster's mean. */
export function clusterSpots(spots: readonly LngLat[], radiusM = ISSUE_CLUSTER_M): LngLat[] {
  if (spots.length === 0) return [];
  const proj = new Projection(spots);
  const r2 = radiusM * radiusM;
  const seeds: Array<{ x: number; y: number; sx: number; sy: number; count: number }> = [];
  const grid = new Map<number, number[]>();
  for (let i = 0; i < spots.length; i++) {
    const x = proj.xs[i];
    const y = proj.ys[i];
    const cx = Math.floor(x / radiusM);
    const cy = Math.floor(y / radiusM);
    let hit = -1;
    for (let dx = -1; dx <= 1 && hit < 0; dx++) {
      for (let dy = -1; dy <= 1 && hit < 0; dy++) {
        for (const c of grid.get(cellKey(cx + dx, cy + dy)) ?? []) {
          const s = seeds[c];
          if ((s.x - x) ** 2 + (s.y - y) ** 2 <= r2) {
            hit = c;
            break;
          }
        }
      }
    }
    if (hit < 0) {
      push(grid, cellKey(cx, cy), seeds.length);
      seeds.push({ x, y, sx: x, sy: y, count: 1 });
    } else {
      seeds[hit].sx += x;
      seeds[hit].sy += y;
      seeds[hit].count += 1;
    }
  }
  return seeds.map((s) => proj.unproject(s.sx / s.count, s.sy / s.count));
}

/**
 * One spot per doubled stretch: overlapping pairs that share a line, or whose
 * lines meet at a vertex, are one stretch. The spot is the pair nearest its middle.
 */
export function overlapSpots(
  issues: Pick<RouteIssues, "overlaps">,
  segments: Segs,
): LngLat[] {
  const overlaps = issues.overlaps;
  if (overlaps.length === 0) return [];
  const parent = new Map<number, number>();
  const find = (s: number): number => {
    let v = s;
    while (parent.get(v) !== v) {
      const up = parent.get(v)!;
      parent.set(v, parent.get(up)!);
      v = up;
    }
    return v;
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };
  for (const o of overlaps) {
    if (!parent.has(o.a)) parent.set(o.a, o.a);
    if (!parent.has(o.b)) parent.set(o.b, o.b);
    union(o.a, o.b);
  }
  const byVertex = new Map<number, number>();
  for (const s of parent.keys()) {
    for (const v of segments[s]) {
      const other = byVertex.get(v);
      if (other == null) byVertex.set(v, s);
      else union(s, other);
    }
  }
  const groups = new Map<number, LngLat[]>();
  for (const o of overlaps) {
    const root = find(o.a);
    const list = groups.get(root);
    if (list) list.push(o.at);
    else groups.set(root, [o.at]);
  }
  return [...groups.values()].map((spots) => {
    const lng = spots.reduce((sum, p) => sum + p[0], 0) / spots.length;
    const lat = spots.reduce((sum, p) => sum + p[1], 0) / spots.length;
    let best = spots[0];
    let bestD = Number.POSITIVE_INFINITY;
    for (const p of spots) {
      const d = (p[0] - lng) ** 2 + (p[1] - lat) ** 2;
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    return [best[0], best[1]];
  });
}

export function crossingSpots(issues: Pick<RouteIssues, "crossings">): LngLat[] {
  return clusterSpots(issues.crossings.map((c) => c.at));
}

/** Bounds of each part except the largest, largest first. */
export function smallerPartBounds(
  points: readonly LngLat[],
  parts: ConnectedParts,
): Array<[LngLat, LngLat]> {
  if (parts.count < 2) return [];
  const boxes = new Map<number, [LngLat, LngLat]>();
  for (let i = 0; i < points.length; i++) {
    const p = parts.partOf[i];
    if (p === parts.largest) continue;
    const [lng, lat] = points[i];
    const box = boxes.get(p);
    if (!box) boxes.set(p, [[lng, lat], [lng, lat]]);
    else {
      box[0] = [Math.min(box[0][0], lng), Math.min(box[0][1], lat)];
      box[1] = [Math.max(box[1][0], lng), Math.max(box[1][1], lat)];
    }
  }
  return [...boxes.entries()].sort((a, b) => parts.sizes[b[0]] - parts.sizes[a[0]]).map(([, box]) => box);
}

export type RouteIssueFeatureKind = "stray" | "overlap" | "gap-link" | "lone" | "gap" | "crossing";

interface IssueFeature {
  type: "Feature";
  properties: { kind: RouteIssueFeatureKind };
  geometry:
    | { type: "MultiLineString"; coordinates: LngLat[][] }
    | { type: "Point"; coordinates: LngLat };
}

function filterDecorativeGaps(
  gaps: GapIssue[],
  segments: Segs,
  decorative?: readonly boolean[],
): GapIssue[] {
  if (!decorative?.length) return gaps;
  return gaps.filter((g) => {
    if (touchesOnlyDecorativeSegments(g.point, segments, decorative)) return false;
    if (g.targetPoint != null && touchesOnlyDecorativeSegments(g.targetPoint, segments, decorative)) return false;
    return true;
  });
}

function isDecorativeSegment(s: number, decorative?: readonly boolean[]): boolean {
  return isDecorative(decorative, s);
}

/** Map overlay: cut-off lines, doubled lines, gap links, and point markers. */
export function routeIssueFeatures(
  points: readonly LngLat[],
  segments: Segs,
  issues: RouteIssues,
  decorative?: readonly boolean[],
): { type: "FeatureCollection"; features: IssueFeature[] } {
  const features: IssueFeature[] = [];
  const lines = (kind: RouteIssueFeatureKind, coordinates: LngLat[][]) => {
    if (coordinates.length > 0) {
      features.push({ type: "Feature", properties: { kind }, geometry: { type: "MultiLineString", coordinates } });
    }
  };
  const mark = (kind: RouteIssueFeatureKind, at: LngLat) => {
    features.push({ type: "Feature", properties: { kind }, geometry: { type: "Point", coordinates: [at[0], at[1]] } });
  };
  const segmentLine = (s: number): LngLat[] => {
    const [a, b] = segments[s];
    return [[points[a][0], points[a][1]], [points[b][0], points[b][1]]];
  };
  const doubled = new Set<number>();
  for (const o of issues.overlaps) {
    doubled.add(o.a);
    doubled.add(o.b);
  }
  lines(
    "overlap",
    [...doubled].filter((s) => !isDecorativeSegment(s, decorative)).map(segmentLine),
  );
  lines(
    "stray",
    issues.straySegments.filter((s) => !isDecorativeSegment(s, decorative)).map(segmentLine),
  );
  const gaps = filterDecorativeGaps(issues.gaps, segments, decorative);
  lines(
    "gap-link",
    gaps.map((g) => [[points[g.point][0], points[g.point][1]], [g.target[0], g.target[1]]]),
  );
  for (const i of issues.lonePoints) {
    if (!touchesOnlyDecorativeSegments(i, segments, decorative)) mark("lone", points[i]);
  }
  for (const g of gaps) mark("gap", points[g.point]);
  for (const c of issues.crossings) mark("crossing", c.at);
  return { type: "FeatureCollection", features };
}

class Projection {
  readonly xs: Float64Array;
  readonly ys: Float64Array;
  private readonly lng0: number;
  private readonly lat0: number;
  private readonly kx: number;

  constructor(points: readonly LngLat[]) {
    let lng0 = 0;
    let lat0 = 0;
    for (const [lng, lat] of points) {
      lng0 += lng;
      lat0 += lat;
    }
    this.lng0 = points.length > 0 ? lng0 / points.length : 0;
    this.lat0 = points.length > 0 ? lat0 / points.length : 0;
    this.kx = M_PER_DEG * Math.cos((this.lat0 * Math.PI) / 180);
    this.xs = new Float64Array(points.length);
    this.ys = new Float64Array(points.length);
    for (let i = 0; i < points.length; i++) {
      this.xs[i] = (points[i][0] - this.lng0) * this.kx;
      this.ys[i] = (points[i][1] - this.lat0) * M_PER_DEG;
    }
  }

  unproject(x: number, y: number): LngLat {
    return [this.lng0 + x / this.kx, this.lat0 + y / M_PER_DEG];
  }
}

const CELL_OFFSET = 1 << 20;

function cellKey(cx: number, cy: number): number {
  return (cx + CELL_OFFSET) * 2 * CELL_OFFSET + (cy + CELL_OFFSET);
}

function push(map: Map<number, number[]>, key: number, value: number): void {
  const list = map.get(key);
  if (list) list.push(value);
  else map.set(key, [value]);
}

/** Lines whose box spans more cells than this are checked against everything instead. */
const LONG_SEGMENT_CELLS = 400;

class SegmentGrid {
  private readonly cells = new Map<number, number[]>();
  readonly long: number[] = [];
  private readonly stamp: Int32Array;
  private tick = 0;
  private readonly cell: number;

  constructor(cell: number, xs: Float64Array, ys: Float64Array, segments: Segs, pad: number) {
    this.cell = cell;
    this.stamp = new Int32Array(segments.length);
    for (let s = 0; s < segments.length; s++) {
      const [a, b] = segments[s];
      const x0 = Math.floor((Math.min(xs[a], xs[b]) - pad) / cell);
      const x1 = Math.floor((Math.max(xs[a], xs[b]) + pad) / cell);
      const y0 = Math.floor((Math.min(ys[a], ys[b]) - pad) / cell);
      const y1 = Math.floor((Math.max(ys[a], ys[b]) + pad) / cell);
      if ((x1 - x0 + 1) * (y1 - y0 + 1) > LONG_SEGMENT_CELLS) {
        this.long.push(s);
        continue;
      }
      for (let cx = x0; cx <= x1; cx++) for (let cy = y0; cy <= y1; cy++) push(this.cells, cellKey(cx, cy), s);
    }
  }

  /** Each line near the box once. Segments are padded at insert time. */
  near(minX: number, minY: number, maxX: number, maxY: number, visit: (s: number) => void): void {
    this.tick += 1;
    const t = this.tick;
    const x0 = Math.floor(minX / this.cell);
    const x1 = Math.floor(maxX / this.cell);
    const y0 = Math.floor(minY / this.cell);
    const y1 = Math.floor(maxY / this.cell);
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const list = this.cells.get(cellKey(cx, cy));
        if (!list) continue;
        for (const s of list) {
          if (this.stamp[s] === t) continue;
          this.stamp[s] = t;
          visit(s);
        }
      }
    }
    for (const s of this.long) {
      if (this.stamp[s] === t) continue;
      this.stamp[s] = t;
      visit(s);
    }
  }
}

/** Closest point on a-b to p, as the segment parameter and squared distance. */
function closestOnSegment(
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): { t: number; d2: number; x: number; y: number } {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  const x = ax + dx * t;
  const y = ay + dy * t;
  return { t, d2: (px - x) ** 2 + (py - y) ** 2, x, y };
}

function findGaps(
  n: number,
  segments: Segs,
  deg: Int32Array,
  xs: Float64Array,
  ys: Float64Array,
  proj: Projection,
  pointGrid: Map<number, number[]>,
  segGrid: SegmentGrid,
  cell: number,
  gapM: number,
): GapIssue[] {
  const head = new Int32Array(n + 1);
  for (let i = 0; i < n; i++) head[i + 1] = head[i] + deg[i];
  const to = new Int32Array(head[n]);
  const fill = head.slice(0, n);
  for (const [a, b] of segments) {
    to[fill[a]++] = b;
    to[fill[b]++] = a;
  }
  // Anything this far along the drawing from the end is its own line, not a near miss.
  const reachM = gapM * 3;
  const dist = new Float64Array(n);
  const seen = new Int32Array(n).fill(-1);
  const gap2 = gapM * gapM;
  const gaps: GapIssue[] = [];
  const byPoint = new Map<number, GapIssue>();
  const heap: number[] = [];

  for (let v = 0; v < n; v++) {
    if (deg[v] !== 1) continue;
    // Bounded Dijkstra: stamp vertices reachable within reachM.
    heap.length = 0;
    seen[v] = v;
    dist[v] = 0;
    heap.push(v);
    const done = new Set<number>();
    while (heap.length > 0) {
      let bi = 0;
      for (let k = 1; k < heap.length; k++) if (dist[heap[k]] < dist[heap[bi]]) bi = k;
      const u = heap[bi];
      heap[bi] = heap[heap.length - 1];
      heap.pop();
      if (done.has(u)) continue;
      done.add(u);
      for (let e = head[u]; e < head[u + 1]; e++) {
        const w = to[e];
        const d = dist[u] + Math.hypot(xs[u] - xs[w], ys[u] - ys[w]);
        if (d > reachM) continue;
        if (seen[w] !== v || d < dist[w]) {
          seen[w] = v;
          dist[w] = d;
          heap.push(w);
        }
      }
    }
    const reached = (i: number) => seen[i] === v;

    let best: GapIssue | null = null;
    let bestD2 = gap2;
    const cx = Math.floor(xs[v] / cell);
    const cy = Math.floor(ys[v] / cell);
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const u of pointGrid.get(cellKey(cx + dx, cy + dy)) ?? []) {
          if (u === v || reached(u)) continue;
          const d2 = (xs[u] - xs[v]) ** 2 + (ys[u] - ys[v]) ** 2;
          if (d2 > bestD2) continue;
          bestD2 = d2;
          best = { point: v, target: proj.unproject(xs[u], ys[u]), targetPoint: u, targetSegment: null, distanceM: 0 };
        }
      }
    }
    segGrid.near(xs[v] - gapM, ys[v] - gapM, xs[v] + gapM, ys[v] + gapM, (s) => {
      const [a, b] = segments[s];
      if (a === v || b === v || reached(a) || reached(b)) return;
      const hit = closestOnSegment(xs[v], ys[v], xs[a], ys[a], xs[b], ys[b]);
      if (hit.d2 >= bestD2) return;
      bestD2 = hit.d2;
      const end = hit.t <= 0 ? a : hit.t >= 1 ? b : null;
      best = {
        point: v,
        target: proj.unproject(hit.x, hit.y),
        targetPoint: end,
        targetSegment: end == null ? s : null,
        distanceM: 0,
      };
    });
    if (!best) continue;
    const found: GapIssue = best;
    found.distanceM = Math.sqrt(bestD2);
    if (found.targetPoint != null && found.targetPoint < v) {
      const mutual = byPoint.get(found.targetPoint);
      if (mutual && mutual.targetPoint === v) continue;
    }
    byPoint.set(v, found);
    gaps.push(found);
  }
  return gaps;
}

function findOverlapsAndCrossings(
  segments: Segs,
  originalIndex: readonly number[],
  xs: Float64Array,
  ys: Float64Array,
  proj: Projection,
  segGrid: SegmentGrid,
  overlapM: number,
  overlapMinM: number,
  sinParallel: number,
): { overlaps: OverlapIssue[]; crossings: CrossingIssue[] } {
  const overlaps: OverlapIssue[] = [];
  const crossings: CrossingIssue[] = [];
  const longSet = new Set(segGrid.long);

  const segId = (local: number) => originalIndex[local] ?? local;

  const check = (i: number, j: number) => {
    const [a0, a1] = segments[i];
    const [b0, b1] = segments[j];
    const ax = xs[a1] - xs[a0];
    const ay = ys[a1] - ys[a0];
    const bx = xs[b1] - xs[b0];
    const by = ys[b1] - ys[b0];
    const la = Math.hypot(ax, ay);
    const lb = Math.hypot(bx, by);
    if (la < 1e-6 || lb < 1e-6) return;
    const cross = ax * by - ay * bx;
    if (Math.abs(cross) <= sinParallel * la * lb) {
      const o = overlapOf(i, j, la >= lb);
      if (o) overlaps.push(o);
      return;
    }
    if (a0 === b0 || a0 === b1 || a1 === b0 || a1 === b1) return;
    // Proper intersection of a0-a1 and b0-b1.
    const ex = xs[b0] - xs[a0];
    const ey = ys[b0] - ys[a0];
    const t = (ex * by - ey * bx) / cross;
    const u = (ex * ay - ey * ax) / cross;
    const eps = 1e-6;
    if (t <= eps || t >= 1 - eps || u <= eps || u >= 1 - eps) return;
    crossings.push({ a: segId(i), b: segId(j), at: proj.unproject(xs[a0] + ax * t, ys[a0] + ay * t) });
  };

  const overlapOf = (i: number, j: number, iIsBase: boolean): OverlapIssue | null => {
    const base = iIsBase ? segments[i] : segments[j];
    const other = iIsBase ? segments[j] : segments[i];
    const ox = xs[base[0]];
    const oy = ys[base[0]];
    const len = Math.hypot(xs[base[1]] - ox, ys[base[1]] - oy);
    const ux = (xs[base[1]] - ox) / len;
    const uy = (ys[base[1]] - oy) / len;
    const along = (k: number) => (xs[k] - ox) * ux + (ys[k] - oy) * uy;
    const side = (k: number) => (xs[k] - ox) * uy - (ys[k] - oy) * ux;
    const t0 = along(other[0]);
    const t1 = along(other[1]);
    if (Math.abs(t1 - t0) < 1e-6) return null;
    const lo = Math.max(0, Math.min(t0, t1));
    const hi = Math.min(len, Math.max(t0, t1));
    const shared = hi - lo;
    const otherLen = Math.hypot(xs[other[1]] - xs[other[0]], ys[other[1]] - ys[other[0]]);
    const need = Math.max(1, Math.min(overlapMinM, 0.5 * Math.min(len, otherLen)));
    if (shared < need) return null;
    const s0 = side(other[0]);
    const s1 = side(other[1]);
    const offsetAt = (pos: number) => s0 + ((pos - t0) / (t1 - t0)) * (s1 - s0);
    if (Math.abs(offsetAt(lo)) > overlapM || Math.abs(offsetAt(hi)) > overlapM) return null;
    const mid = (lo + hi) / 2;
    const off = offsetAt(mid) / 2;
    const a = segId(Math.min(i, j));
    const b = segId(Math.max(i, j));
    return {
      a,
      b,
      at: proj.unproject(ox + ux * mid + uy * off, oy + uy * mid - ux * off),
      lengthM: shared,
    };
  };

  for (let i = 0; i < segments.length; i++) {
    const [a, b] = segments[i];
    if (longSet.has(i)) {
      for (let j = i + 1; j < segments.length; j++) check(i, j);
      continue;
    }
    segGrid.near(
      Math.min(xs[a], xs[b]),
      Math.min(ys[a], ys[b]),
      Math.max(xs[a], xs[b]),
      Math.max(ys[a], ys[b]),
      (j) => {
        if (j > i) check(i, j);
      },
    );
  }
  return { overlaps, crossings };
}
